/**
 * Cascade-delete an encounter and all linked records.
 */
import { partitionDeletablePatientFiles, buildPatientFileLocks } from '@helios/data-access';

export interface DeleteEncounterParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: any;
  orgId: string;
  encounterId: string;
  deleteAppointment?: boolean;
  deleteQueueEntry?: boolean;
}

export interface DeleteEncounterResult {
  deletedEncounter: boolean;
  deletedAppointment: boolean;
  deletedQueueEntry: boolean;
  error?: string;
}

export async function deleteEncounterCascade(
  params: DeleteEncounterParams,
): Promise<DeleteEncounterResult> {
  const { db, encounterId, deleteAppointment = false, deleteQueueEntry = false } = params;

  const result: DeleteEncounterResult = {
    deletedEncounter: false,
    deletedAppointment: false,
    deletedQueueEntry: false,
  };

  // 1. Resolve the encounter and its linkages
  const encRows = await db.getAll(
    `SELECT id, visit_id, appointment_id, queue_id FROM patient_encounters WHERE id = ?`,
    [encounterId],
  );

  if (encRows.length === 0) {
    result.error = 'Encounter not found';
    return result;
  }

  const enc = encRows[0] as {
    id: string;
    visit_id: string | null;
    appointment_id: string | null;
    queue_id: string | null;
  };

  // 2. Find all invoice IDs linked to this encounter
  const invoiceRows = (await db.getAll(`SELECT id FROM invoices WHERE encounter_id = ?`, [
    encounterId,
  ])) as { id: string }[];
  const invoiceIds = invoiceRows.map((r: { id: string }) => r.id);

  // 3. Find all form_instance IDs linked to this encounter
  const formRows = (await db.getAll(`SELECT id FROM form_instances WHERE encounter_id = ?`, [
    encounterId,
  ])) as { id: string }[];
  const formInstanceIds = formRows.map((r: { id: string }) => r.id);

  // 4. Safety check for appointment: don't delete if shared with other encounters
  let safeToDeleteAppointment = false;
  if (deleteAppointment && enc.appointment_id) {
    const otherEncs = await db.getAll(
      `SELECT id FROM patient_encounters WHERE appointment_id = ? AND id != ?`,
      [enc.appointment_id, encounterId],
    );
    safeToDeleteAppointment = (otherEncs as unknown[]).length === 0;
  }

  // 5. Execute cascade deletes
  // Delete invoice_payments for linked invoices
  for (const invId of invoiceIds) {
    await db.execute(`DELETE FROM invoice_payments WHERE invoice_id = ?`, [invId]);
  }

  // Delete invoice_items for linked invoices
  for (const invId of invoiceIds) {
    await db.execute(`DELETE FROM invoice_items WHERE invoice_id = ?`, [invId]);
  }

  // Delete invoices
  await db.execute(`DELETE FROM invoices WHERE encounter_id = ?`, [encounterId]);

  // Delete treatment records
  await db.execute(`DELETE FROM patient_treatment_records WHERE encounter_id = ?`, [encounterId]);

  // Delete form_instance children
  for (const fiId of formInstanceIds) {
    await db.execute(`DELETE FROM form_instance_values WHERE form_instance_id = ?`, [fiId]);
    await db.execute(`DELETE FROM form_instance_signatures WHERE form_instance_id = ?`, [fiId]);
  }

  // Delete form_instances
  await db.execute(`DELETE FROM form_instances WHERE encounter_id = ?`, [encounterId]);

  // Delete patient_notes
  await db.execute(`DELETE FROM patient_notes WHERE encounter_id = ?`, [encounterId]);

  // Delete patient_files — EXCEPT published laboratory results (PRD.305 §D4).
  // A published result is a clinical artifact of the patient, not of the
  // encounter, so deleting the visit note must not take it with it. The split
  // uses the same shared guard the interactive Files surfaces use.
  const encounterFiles = (await db.getAll(`SELECT id FROM patient_files WHERE encounter_id = ?`, [
    encounterId,
  ])) as { id: string }[];
  const lockedRows = (await db.getAll(
    `SELECT DISTINCT f.patient_file_id AS patient_file_id
       FROM lab_result_files f
       JOIN lab_results r ON r.id = f.lab_result_id
      WHERE r.published_at IS NOT NULL`,
  )) as { patient_file_id: string | null }[];
  const { deletable, protected: protectedFileIds } = partitionDeletablePatientFiles(
    encounterFiles.map((f) => f.id),
    buildPatientFileLocks(lockedRows),
  );
  for (const fileId of deletable) {
    await db.execute(`DELETE FROM patient_files WHERE id = ?`, [fileId]);
  }
  if (protectedFileIds.length > 0) {
    // Detach rather than delete, so the artifact survives in the patient's
    // Files library and in Labs with its lab linkage intact.
    for (const fileId of protectedFileIds) {
      await db.execute(
        `UPDATE patient_files SET encounter_id = NULL, updated_at = ? WHERE id = ?`,
        [new Date().toISOString(), fileId],
      );
    }
  }

  // Delete patient_vital_entries
  await db.execute(`DELETE FROM patient_vital_entries WHERE encounter_id = ?`, [encounterId]);

  // Delete odontogram_charts
  await db.execute(`DELETE FROM odontogram_charts WHERE encounter_id = ?`, [encounterId]);

  // 6. Optional: delete queue entry
  if (deleteQueueEntry && enc.queue_id) {
    await db.execute(`DELETE FROM queue_entries WHERE id = ?`, [enc.queue_id]);
    result.deletedQueueEntry = true;
  }

  // 7. Optional: delete appointment (only if safe)
  if (deleteAppointment && enc.appointment_id) {
    if (safeToDeleteAppointment) {
      await db.execute(`DELETE FROM appointments WHERE id = ?`, [enc.appointment_id]);
      result.deletedAppointment = true;
    } else {
      result.error = 'Appointment is linked to other encounters — skipped deletion';
    }
  }

  // 8. Delete the encounter itself
  await db.execute(`DELETE FROM patient_encounters WHERE id = ?`, [encounterId]);
  result.deletedEncounter = true;

  // 9. Check if the visit has any other encounters — if not, delete the visit too
  if (enc.visit_id) {
    const otherVisitEncs = await db.getAll(
      `SELECT id FROM patient_encounters WHERE visit_id = ? AND id != ?`,
      [enc.visit_id, encounterId],
    );
    if ((otherVisitEncs as unknown[]).length === 0) {
      await db.execute(`DELETE FROM patient_visits WHERE id = ?`, [enc.visit_id]);
    }
  }

  return result;
}
